/**
 * Input Component
 * A styled input field component with WCAG 2.1 AA accessibility support.
 * Supports error states with aria-describedby for screen reader announcements.
 * @packageDocumentation
 */

import { useId, forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Props for the Input component.
 */
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  hint?: string;
  error?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ type, hint, error, className, id: providedId, 'aria-describedby': ariaDescribedBy, ...props }, ref) => {
    const generatedId = useId();
    const inputId = providedId ?? generatedId;
    const errorId = error ? `${inputId}-error` : undefined;
    const hintId = hint && !error ? `${inputId}-hint` : undefined;

    const describedByIds = [errorId, hintId, ariaDescribedBy].filter(Boolean).join(' ') || undefined;

    return (
      <div className="w-full">
        <input
          id={inputId}
          type={type}
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedByIds}
          className={cn(
            'flex h-[36px] w-full rounded-lg border bg-transparent px-3 py-1 text-base shadow-sm transition-[border-color,box-shadow] duration-150',
            'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'md:text-sm',
            error ? 'border-destructive focus-visible:ring-destructive' : 'border-input',
            className,
          )}
          {...props}
        />
        {hint && !error && (
          <p id={hintId} className="mt-1.5 text-xs text-muted-foreground">
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} className="mt-1.5 text-xs text-destructive" role="alert" aria-live="polite">
            {error}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
